import axios, { type AxiosInstance } from "axios";
import type { Report, ReportType } from "../ReportsData";

/**
 * Kontrol Edge Reports HTTP API.
 *
 * The shared `useAxiosAuth0()` client already points at `${REACT_APP_API_URL}/v1`
 * and attaches the Auth0 bearer, so every path here is relative to that and no
 * token is ever threaded through by hand.
 *
 * `tenantId` travels as a query param on every call: a Global Admin or Partner
 * reads and generates on behalf of the tenant the dashboard's tenant lens
 * (`state.selection.tenantId`) points at, which is not the tenant in their JWT.
 */

const REPORTS_PATH = "/kontroledge/reports";

/** Standard backend envelope. Some KE endpoints answer a bare payload instead. */
interface ReportsApiEnvelope<T> {
  success?: boolean;
  message?: string;
  data?: T;
}

export interface ReportDownload {
  downloadUrl: string;
  filename?: string;
}

export interface GenerateReportBody {
  year: number;
  month: number;
  /**
   * Optional on the wire (CTX-782): the backend defaults to `detailed`, which
   * is what the monthly job produces.
   */
  reportType?: ReportType;
}

/**
 * Unwrap `{ data }` while tolerating a bare payload — the list endpoint has
 * answered both shapes, and the page must not blank out over the difference.
 */
function unwrapEnvelope<T>(payload: ReportsApiEnvelope<T> | T | undefined): T | undefined {
  if (payload && typeof payload === "object" && "data" in payload) {
    return (payload as ReportsApiEnvelope<T>).data;
  }
  return payload as T | undefined;
}

/** GET `/kontroledge/reports` — every report visible to the selected tenant. */
export async function fetchReportsList(
  api: AxiosInstance,
  tenantId: number
): Promise<Report[]> {
  const response = await api.get<ReportsApiEnvelope<Report[]> | Report[]>(
    REPORTS_PATH,
    { params: { tenantId } }
  );
  const list = unwrapEnvelope<Report[]>(response.data);
  return Array.isArray(list) ? list : [];
}

/**
 * GET `/kontroledge/reports/:id/download` — a short-lived signed URL, not the
 * file itself; the caller navigates to it to start the download.
 */
export async function fetchReportDownload(
  api: AxiosInstance,
  reportId: number,
  tenantId: number
): Promise<ReportDownload> {
  const response = await api.get<
    ReportsApiEnvelope<ReportDownload> | ReportDownload
  >(`${REPORTS_PATH}/${reportId}/download`, { params: { tenantId } });

  const download = unwrapEnvelope<ReportDownload>(response.data);
  if (!download?.downloadUrl) {
    throw new Error("Download URL not found in response");
  }
  return download;
}

/**
 * POST `/kontroledge/reports/generate` — answers `202` and generates in the
 * background, so the caller gets no file here: it re-reads the list until the
 * new row settles.
 */
export async function requestReportGeneration(
  api: AxiosInstance,
  body: GenerateReportBody,
  tenantId: number
): Promise<void> {
  await api.post(REPORTS_PATH + "/generate", body, { params: { tenantId } });
}

/**
 * The message the backend meant the user to read, falling back to the
 * transport error and then to `fallback`. Typed through `isAxiosError` so the
 * feature never reaches into an `any` to find it.
 */
export function readApiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError<ReportsApiEnvelope<unknown>>(error)) {
    return error.response?.data?.message || error.message || fallback;
  }
  if (error instanceof Error) return error.message || fallback;
  return fallback;
}
